import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: '神社概要' },
    { path: '/chushingura', label: '忠臣蔵について' },
    { path: '/festivals', label: '祭礼行事' },
    { path: '/talismans', label: '授与品' },
    { path: '/access', label: 'アクセス' },
    { path: '/contact', label: '問い合わせ' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-black/80'
        }`}
      >
        <div className="w-full px-4 md:px-8 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 md:gap-3 group">
              <img 
                src="https://public.readdy.ai/ai/img_res/442b38a4-338a-4f74-a271-fc262378c9b4.png" 
                alt="大石神社" 
                className="h-8 w-8 md:h-10 md:w-10 object-contain transition-transform group-hover:scale-110"
              />
              <span className="text-white font-serif text-lg md:text-xl tracking-wider group-hover:text-[#D4AF37] transition-colors">
                大石神社
              </span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm tracking-wide transition-colors relative group ${
                    location.pathname === link.path
                      ? 'text-[#D4AF37]'
                      : 'text-white hover:text-[#D4AF37]'
                  }`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#D4AF37] transition-all group-hover:w-full"></span>
                </Link>
              ))}
              
              <Link
                to="/prayer-reservation"
                className="px-6 py-2 border border-[#D4AF37] text-white text-sm tracking-wide rounded-full hover:bg-[#D4AF37] hover:text-black transition-all whitespace-nowrap cursor-pointer"
              >
                祈祷予約
              </Link>

              {user ? (
                <button
                  onClick={() => navigate('/mypage')}
                  className="text-[#D4AF37] hover:text-white transition-colors text-sm tracking-wide cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-user-line"></i>
                  マイページ
                </button>
              ) : (
                <button
                  onClick={() => navigate('/auth/login')}
                  className="bg-[#D4AF37] text-black px-6 py-2 rounded-md hover:bg-[#C4A037] transition-all text-sm tracking-wide cursor-pointer whitespace-nowrap"
                >
                  ログイン
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="メニュー"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>
            <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-[#0A0A0A] shadow-2xl overflow-y-auto">
              <div className="pt-20 pb-8 px-6">
                <div className="space-y-1 mb-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`block py-3 px-4 rounded-lg transition-colors ${
                        location.pathname === link.path
                          ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                          : 'text-white hover:bg-white/5'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>

                <div className="space-y-3 pt-6 border-t border-gray-800">
                  <Link
                    to="/prayer-reservation"
                    className="block w-full py-3 px-4 border border-[#D4AF37] text-white text-center rounded-lg hover:bg-[#D4AF37] hover:text-black transition-all cursor-pointer"
                  >
                    祈祷予約
                  </Link>

                  {user ? (
                    <button
                      onClick={() => navigate('/mypage')}
                      className="w-full py-3 px-4 text-[#D4AF37] border border-[#D4AF37] rounded-lg hover:bg-[#D4AF37] hover:text-black transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      <i className="ri-user-line"></i>
                      マイページ
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/auth/login')}
                      className="w-full py-3 px-4 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A037] transition-all cursor-pointer"
                    >
                      ログイン
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
